import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComment, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { Multiselect } from 'multiselect-react-dropdown';
import PropTypes from 'prop-types';
import AltErrorWrapper
  from '@avtechsoftware/react-component-library/src/components/common/FormErrorHandler/AltErrorWrapper/AltErrorWrapper';
import FormErrorHandler
  from '@avtechsoftware/react-component-library/src/components/common/FormErrorHandler/FormErrorHandler';
import RepeatInterval from './RepeatInterval';
import MaxRepeat from './MaxRepeat';
import QuietTimesDropdown from './QuietTimesDropdown';
import MessageTemplateDropdown from './MessageTemplateDropdown';
import ActionDelayDropdown from './ActionDelayDropdown';

function NotificationAction({
  index = 0,
  errorList,
  action,
  onUpdate,
  onDelete,
  notificationGroups,
  hasAdvancedAlerting,
  showNewGroupModal,
  showNewScheduleModal,
  showNewTemplateModal,
  canDelete = true,
}) {
  const [localAction, setLocalAction] = useState(action);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    setLocalAction(action);
  }, [action]);

  const getOptions = () => {
    if (notificationGroups && notificationGroups.length) {
      const opts = notificationGroups.map((group) => ({ name: group.name, id: group.id }));
      opts.push({ name: '- Create New Group -', id: 0 }); // pseudo-option
      return opts;
    }
    return [{ name: '- Create New Group -', id: 0 }];
  };

  const handleChange = (field, value) => {
    const updated = { ...localAction, [field]: value };
    setLocalAction(updated);
    onUpdate(updated);
  };

  const onSelect = (selected) => {
    const createNewSelected = selected.find((s) => s.id === 0);
    if (createNewSelected) {
      showNewGroupModal(action.id);

      // Workaround to get Multiselect to reset selection after "Create New Group" is chosen
      setRefreshKey((prev) => prev + 1);
      return;
    }
    handleChange('notificationGroupIdList', selected.map((s) => s.id));
  };

  const onRemove = (removedList) => {
    handleChange('notificationGroupIdList', removedList.map((r) => r.id));
  };

  const selectedGroups = (notificationGroups || [])
    .filter((group) => (localAction.notificationGroupIdList || [])
      .includes(group.id))
    .map((group) => ({ name: group.name, id: group.id }));

  return (
    <>
      <FormErrorHandler
        errorList={errorList}
        fieldList={[`actions[${index}].notificationGroupIdList`,
          `actions[${index}].delay`,
          `actions[${index}].repeatInterval`,
          `actions[${index}].maxReminders`,
          `actions[${index}].quietTimeId`,
          `actions[${index}].templateId`]}
      >
        <div className="row d-flex flex-wrap align-items-center pl-3 pr-3" style={{ height: '40px' }}>
          <span className="col-10">
            <FontAwesomeIcon icon={faComment} size="lg" />
            <span className="h4 ml-3">Notification Action</span>
          </span>
          {
            canDelete && (
            <span className="col-2" role="button" onClick={onDelete} tabIndex={0}>
              <FontAwesomeIcon className="float-right" icon={faTrash} />
            </span>
            )
          }
        </div>

        <div className="row mt-2">
          <span className="col-10 ml-5" aria-label="notification groups multi-select">
            {!hasAdvancedAlerting && (
            <div className="text-muted mb-1" style={{ fontSize: '12px' }}>
              {/* eslint-disable-next-line max-len */}
              Your current account doesn&apos;t support multiple notification groups for an alert action.
              {' '}
              <a href="/s/subscription/billing/change-plan">Upgrade</a>
            </div>
            )}
            <AltErrorWrapper fieldId={`actions[${index}].notificationGroupIdList`}>
              <Multiselect
                key={`ms-${refreshKey}`}
                options={getOptions()}
                selectedValues={selectedGroups}
                onSelect={onSelect}
                onRemove={onRemove}
                displayValue="name"
                closeOnSelect
                selectionLimit={hasAdvancedAlerting ? undefined : 1}
              />
            </AltErrorWrapper>
          </span>
        </div>

        <div className="row d-flex flex-wrap align-items-center mt-2 mb-2 pr-3">
          <span className="row col-12 ml-5">
            <div className="mr-3 d-inline-block">
              <AltErrorWrapper fieldId={`actions[${index}].delay`}>
                <ActionDelayDropdown
                  disabled={!hasAdvancedAlerting}
                  updateField={handleChange}
                  value={localAction.delay}
                />
              </AltErrorWrapper>
            </div>
            <div className="mr-2 d-inline-block">
              <AltErrorWrapper fieldId={`actions[${index}].repeatInterval`}>
                <RepeatInterval
                  updateField={handleChange}
                  active={localAction.repeatEnabled && hasAdvancedAlerting}
                  value={localAction.repeatInterval}
                  disabled={!hasAdvancedAlerting}
                />
              </AltErrorWrapper>
            </div>
            <div className="mr-3 d-inline-block">
              <AltErrorWrapper fieldId={`actions[${index}].maxReminders`}>
                <MaxRepeat
                  active={localAction.maxRepeatEnabled}
                  value={localAction.maxReminders}
                  updateField={handleChange}
                  disabled={!hasAdvancedAlerting}
                />
              </AltErrorWrapper>
            </div>
            <div className="mr-3 d-inline-block">
              <AltErrorWrapper fieldId={`actions[${index}].quietTimeId`}>
                <QuietTimesDropdown
                  createSchedule={() => showNewScheduleModal(localAction.id)}
                  quietTimeId={localAction.quietTimeId}
                  updateField={handleChange}
                  disabled={!hasAdvancedAlerting}
                />
              </AltErrorWrapper>
            </div>
            <div className="mr-3 d-inline-block">
              <AltErrorWrapper fieldId={`actions[${index}].templateId`}>
                <MessageTemplateDropdown
                  createTemplate={() => showNewTemplateModal(localAction.id)}
                  alertEscalationActionTemplateSetId={localAction.templateId}
                  updateField={handleChange}
                  disabled={!hasAdvancedAlerting}
                />
              </AltErrorWrapper>
            </div>
          </span>
        </div>
      </FormErrorHandler>
    </>
  );
}
NotificationAction.defaultProps = {
  index: 0,
  canDelete: true,
};
NotificationAction.propTypes = {
  index: PropTypes.number,
  // eslint-disable-next-line react/forbid-prop-types
  errorList: PropTypes.array.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  action: PropTypes.object.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  notificationGroups: PropTypes.arrayOf(
    PropTypes.object,
  ).isRequired,
  hasAdvancedAlerting: PropTypes.bool.isRequired,
  showNewGroupModal: PropTypes.func.isRequired,
  showNewScheduleModal: PropTypes.func.isRequired,
  showNewTemplateModal: PropTypes.func.isRequired,
  canDelete: PropTypes.bool,
};
export default NotificationAction;
